import { useCallback, useSyncExternalStore } from 'react'
import type { SortCriterion, SortState } from './types'

const STATE_PREFIX = 'easycrm:table:'
const STATE_SUFFIX = ':state'
const SAME_TAB_EVENT = 'easycrm:table-state-changed'

export type FilterValue =
  | { kind: 'text'; value: string }
  | { kind: 'select'; values: string[] }
  | { kind: 'dateRange'; from: string | null; to: string | null }
  | { kind: 'bool'; value: boolean | null }

export interface TableState {
  sort: SortState
  filters: Record<string, FilterValue>
  search: string
  pageOffset: number
  pageLimit: number
}

export const DEFAULT_PAGE_LIMIT = 50

const EMPTY_STATE: TableState = {
  sort: [],
  filters: {},
  search: '',
  pageOffset: 0,
  pageLimit: DEFAULT_PAGE_LIMIT,
}

const cache = new Map<string, TableState>()

function stateKey(tableId: string): string {
  return `${STATE_PREFIX}${tableId}${STATE_SUFFIX}`
}

function readFromStorage(tableId: string): TableState {
  try {
    const raw = localStorage.getItem(stateKey(tableId))
    if (!raw) return EMPTY_STATE
    const parsed = JSON.parse(raw) as Partial<TableState>
    return {
      sort: Array.isArray(parsed.sort) ? (parsed.sort as SortCriterion[]) : [],
      filters: parsed.filters && typeof parsed.filters === 'object' ? parsed.filters : {},
      search: typeof parsed.search === 'string' ? parsed.search : '',
      pageOffset: typeof parsed.pageOffset === 'number' ? parsed.pageOffset : 0,
      pageLimit: typeof parsed.pageLimit === 'number' && parsed.pageLimit > 0 ? parsed.pageLimit : DEFAULT_PAGE_LIMIT,
    }
  } catch {
    return EMPTY_STATE
  }
}

function getSnapshot(tableId: string): TableState {
  const cached = cache.get(tableId)
  if (cached) return cached
  const fresh = readFromStorage(tableId)
  cache.set(tableId, fresh)
  return fresh
}

function writeState(tableId: string, next: TableState): void {
  cache.set(tableId, next)
  try {
    localStorage.setItem(stateKey(tableId), JSON.stringify(next))
  } catch {
    // localStorage unavailable - state persists in cache for this tab session only
  }
  window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, { detail: { tableId } }))
}

function subscribe(tableId: string, callback: () => void): () => void {
  const sameTabHandler = (e: Event) => {
    if ((e as CustomEvent).detail?.tableId === tableId) callback()
  }
  const storageHandler = (e: StorageEvent) => {
    if (e.key === stateKey(tableId)) {
      cache.delete(tableId)
      callback()
    }
  }
  window.addEventListener(SAME_TAB_EVENT, sameTabHandler)
  window.addEventListener('storage', storageHandler)
  return () => {
    window.removeEventListener(SAME_TAB_EVENT, sameTabHandler)
    window.removeEventListener('storage', storageHandler)
  }
}

/**
 * Per-table persisted state (sort, filters, search, pagination).
 * Stored in localStorage under `easycrm:table:{tableId}:state` and synced across
 * components in the same tab via a CustomEvent + cross-tab via the storage event.
 *
 * Mutations that change the result set (sort, filter, search) reset pageOffset to 0.
 * pageLimit changes preserve the offset within bounds.
 */
export function useTableState(tableId: string) {
  const state = useSyncExternalStore(
    (cb) => subscribe(tableId, cb),
    () => getSnapshot(tableId),
    () => EMPTY_STATE,
  )

  const setSort = useCallback(
    (sort: SortState) => {
      const cur = getSnapshot(tableId)
      writeState(tableId, { ...cur, sort, pageOffset: 0 })
    },
    [tableId],
  )

  const setSearch = useCallback(
    (search: string) => {
      const cur = getSnapshot(tableId)
      writeState(tableId, { ...cur, search, pageOffset: 0 })
    },
    [tableId],
  )

  const setFilter = useCallback(
    (key: string, value: FilterValue) => {
      const cur = getSnapshot(tableId)
      writeState(tableId, {
        ...cur,
        filters: { ...cur.filters, [key]: value },
        pageOffset: 0,
      })
    },
    [tableId],
  )

  const removeFilter = useCallback(
    (key: string) => {
      const cur = getSnapshot(tableId)
      const next = { ...cur.filters }
      delete next[key]
      writeState(tableId, { ...cur, filters: next, pageOffset: 0 })
    },
    [tableId],
  )

  const clearFilters = useCallback(() => {
    const cur = getSnapshot(tableId)
    writeState(tableId, { ...cur, filters: {}, search: '', pageOffset: 0 })
  }, [tableId])

  const setPage = useCallback(
    (next: { offset: number; limit: number }) => {
      const cur = getSnapshot(tableId)
      writeState(tableId, { ...cur, pageOffset: next.offset, pageLimit: next.limit })
    },
    [tableId],
  )

  const reset = useCallback(() => writeState(tableId, EMPTY_STATE), [tableId])

  return { state, setSort, setSearch, setFilter, removeFilter, clearFilters, setPage, reset }
}

/** True when the user has any active filter or search (used to render the chip bar). */
export function hasActiveFilters(state: TableState): boolean {
  if (state.search.trim() !== '') return true
  return Object.keys(state.filters).length > 0
}
